import { SkapiError } from "skapi-js";
import { ref } from "vue";
import router from "@/router";
import { fullScreenLoader, removeFullScreenLoader } from "@/components/jsComponents";
import { user } from "@/code/user";
import { track } from "@/user_tracker";
import { skapi } from "@/main";
import { beginMcpOAuthOnLogin } from "@/code/mcp_oauth";

async function getSkapi() {
    const main = await import("@/main");
    return main.skapi;
}

function getRawQueryParam(name: string): string | null {
    const match = window.location.search.match(new RegExp(`[?&]${name}=([^&]+)`));
    return match ? decodeURIComponent(match[1]) : null;
}

const urlParams = new URLSearchParams(window.location.search);
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID; // Replace this with your actual client ID
export const openIdLoginError = ref<string | null>(null);

const GOOGLE_OAUTH_IN_PROGRESS_KEY = "google_oauth_in_progress";
const GOOGLE_OAUTH_REDIRECT_URI_KEY = "google_oauth_redirect_uri";

function clearGoogleOAuthStart() {
    window.sessionStorage.removeItem(GOOGLE_OAUTH_IN_PROGRESS_KEY);
    window.sessionStorage.removeItem(GOOGLE_OAUTH_REDIRECT_URI_KEY);
}

// Read the inbound OAuth params (e.g. from MCP/chatgpt) that were stashed in
// sessionStorage by handleSkapiOAuthInbound under "oauth:<state>". Removes
// the entry once read. Returns null if absent or unparseable.
function readStashedInboundOAuth(state: string): any | null {
    const key = "oauth:" + state;
    const stored = window.sessionStorage.getItem(key);
    if (!stored) return null;
    window.sessionStorage.removeItem(key);
    try {
        return JSON.parse(stored);
    } catch {
        return null;
    }
}

export function genOAuthCallbackUrl(oauthState: string, session: any, oauthParams?: any): string {
    let oauthRedirectUri = "";

    // console.log("oauthParams from sessionStorage:", oauthParams);
    if (oauthParams) {
        oauthRedirectUri = oauthParams.redirect_uri || "";
    }

    let code = {
        access_token: session.accessToken.jwtToken,
        refresh_token: session.refreshToken.token,
        id_token: session.idToken.jwtToken,
    }

    // Base64 encode to make it URL-safe
    const encodedCode = btoa(JSON.stringify(code));

    return oauthRedirectUri += (oauthRedirectUri.includes("?") ? "&" : "?") + "code=" + encodeURIComponent(encodedCode) + "&state=" + encodeURIComponent(oauthState);
}

export function googleLogin(e: Event, trackTarget: string, useExistingState = false) {
    e.preventDefault();
    openIdLoginError.value = null;

    let url_split = window.location.href.split("?");
    let REDIRECT_URL = url_split[0];
    let rnd;
    
    if (useExistingState) {
        // If useExistingState is true, try to reuse the existing state parameter from the URL
        const urlParams = new URLSearchParams(window.location.search);
        // If the state parameter exists, use it; otherwise, throw error
        if (!urlParams.get("state")) {
            console.error("No state parameter found in URL to reuse.");
            alert("Error: No state parameter found in URL to reuse.");
            return;
        }
        rnd = urlParams.get("state");
    } else {
        rnd = Math.random().toString(36).substring(2); // Generate a random string
    }

    // console.log("Redirect URL:", REDIRECT_URL);
    // Build link to login page
    let url = "https://accounts.google.com/o/oauth2/v2/auth";
    url += "?client_id=" + GOOGLE_CLIENT_ID;
    url += "&redirect_uri=" + encodeURIComponent(REDIRECT_URL);
    url += "&response_type=code";
    url +=
        "&scope=" +
        encodeURIComponent(
            "https://www.googleapis.com/auth/userinfo.profile https://www.googleapis.com/auth/userinfo.email",
        );
    url += "&prompt=consent";
    url += "&state=" + encodeURIComponent(rnd);
    url += "&access_type=offline";

    window.sessionStorage.setItem(GOOGLE_OAUTH_IN_PROGRESS_KEY, "1");
    window.sessionStorage.setItem(GOOGLE_OAUTH_REDIRECT_URI_KEY, REDIRECT_URL);

    track(
        {
            user_id: user?.user_id || "guest",
            target: trackTarget,
        },
        () => {
            window.location.href = url;
        },
    );
}

// console.log("Google Client ID:", GOOGLE_CLIENT_ID);

/**
 * INBOUND — this app acting as an Identity Provider.
 *
 * Triggered when an external app (e.g. an MCP server) redirected the browser
 * here with `?oauth=platform&redirect_uri=...&state=...`. After the user logs in
 * we package the skapi session tokens and bounce the browser back to the
 * caller's `redirect_uri` with a `code` + `state` querystring.
 *
 * Side effects:
 *  - sessionStorage["oauth:<state>"] = serialized inbound query (so a later
 *    Google round-trip can still recover redirect_uri).
 *  - window.location.href is replaced with the caller's redirect URL.
 *
 * @returns 'redirecting' if the browser is leaving, otherwise undefined.
 */
export const handleSkapiOAuthInbound = async (): Promise<'redirecting' | void> => {
    // webpage is redirected from mcp server with oauth query parameter
    const requiredParams = ["redirect_uri", "state"];
    for (const param of requiredParams) {
        if (!urlParams.get(param)) {
            console.error(`Missing required OAuth parameter: ${param}`);
            return;
        }
    }

    const urlParamsJson = Object.fromEntries(urlParams.entries());
    const state = urlParams.get("state");
    window.sessionStorage.setItem("oauth:" + state, JSON.stringify(urlParamsJson));

    if (!state) return;

    const session = skapi.session;
    // Not logged in yet: keep the inbound query stashed and stay on the
    // login screen. After successful login, login.vue's returnOAuthToMCP()
    // will pick the params back up from sessionStorage and redirect to the
    // caller's redirect_uri with a fresh session-derived code.
    if (!session?.accessToken?.jwtToken) {
        return;
    }

    window.location.href = genOAuthCallbackUrl(state, session, urlParamsJson);
    return 'redirecting';
};

/**
 * OUTBOUND — this app returning from Google's authorization endpoint.
 *
 * Triggered when Google redirected the browser back here with `?code=...`
 * AND we previously set `google_oauth_in_progress=1` in sessionStorage from
 * `googleLogin()`. We exchange the code for an access token via skapi's
 * `clientSecretRequest`, sign the user in via `skapi.openIdLogin`, optionally
 * merge a pre-existing skapi account, then redirect either to the IdP caller
 * (when `state` is present) or to `/my-projects`.
 *
 * @returns 'redirecting' if the browser is leaving, otherwise undefined.
 */
export const handleGoogleOAuthReturn = async (): Promise<'redirecting' | void> => {
    if (!urlParams.get("code")) return;

    const oauthInProgress = window.sessionStorage.getItem(GOOGLE_OAUTH_IN_PROGRESS_KEY) === "1";
    if (!oauthInProgress) return;

    // the webpage is loaded, and it's redirected from the Google login page.
        fullScreenLoader();
        const skapi = await getSkapi();

        let REDIRECT_URL = window.sessionStorage.getItem(GOOGLE_OAUTH_REDIRECT_URI_KEY) || window.location.href.split("?")[0]; // Must match redirect_uri used when login started
        const code = getRawQueryParam("code") || urlParams.get("code");

        function removeLoader() {
            removeFullScreenLoader();

            // remove get parameters from URL
            const newUrl = window.location.pathname;

            // Use router to replace URL without reloading
            router.replace(newUrl);
        }

        // Safely retrieve access token using clientSecretRequest
        const csrData = skapi.clientSecretRequest({
            clientSecretName: "ggl",
            url: "https://oauth2.googleapis.com/token",
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
            },
            data: {
                code: code,
                client_id: GOOGLE_CLIENT_ID,
                client_secret: "$CLIENT_SECRET",
                redirect_uri: REDIRECT_URL,
                grant_type: "authorization_code",
            },
        });

        let data = await csrData.catch((err) => {
            if (!(err instanceof SkapiError)) {
                removeLoader();
                throw err;
            }

            // INVALID_REQUEST는 clientSecretRequest 에러
            if (err?.code === "INVALID_REQUEST") {
                openIdLoginError.value =
                    err.message || "Invalid request for Google login.";
                removeLoader();
                return;
            }

            if (err.message.includes("NOT_EXISTS")) {
                openIdLoginError.value = "The account does not exist.";
                removeLoader();
                return;
            }

            // openIdLogin에서 throw된 에러면 이미 메시지 설정됨, 글로벌 핸들러로 전파
            // clientSecretRequest의 예상치 못한 에러는 메시지 설정 후 전파
            if (!openIdLoginError.value) {
                openIdLoginError.value =
                    "Google login failed. Please try again.";
                removeLoader();
            }

            throw err;
        });

        // data.then(async (data: any) => {
        // await router.isReady();

        if (data.error) {
            if (import.meta.env.DEV) {
                console.error(data);
            }

            // Google OAuth error 처리
            let errorData = data.error;
            if (typeof errorData === "string") {
                try {
                    errorData = JSON.parse(errorData);
                } catch (e) {
                    errorData = { error: data.error };
                }
            }

            switch (errorData.error) {
                case "invalid_grant":
                    openIdLoginError.value =
                        "Google login session expired. Please try again.";
                    break;
                case "access_denied":
                    openIdLoginError.value = "Google login was denied.";
                    break;
                default:
                    openIdLoginError.value =
                        "Google login failed. Please try again.";
            }

            clearGoogleOAuthStart();
            removeLoader();
            return;
        }

        openIdLoginError.value = null;
        let oauthRedirectUri = "";
        
        // Use openIdLogin to log in
        try {
            await skapi.openIdLogin({
                id: "by_skapi",
                token: data.access_token,
            });

            if(urlParams.get("state")) {
                const state = urlParams.get("state")!;
                const stashed = readStashedInboundOAuth(state);
                if (stashed) {
                    oauthRedirectUri = genOAuthCallbackUrl(state, skapi.session, stashed);
                    clearGoogleOAuthStart();
                    window.location.href = oauthRedirectUri;
                    return 'redirecting';
                }
                oauthRedirectUri = genOAuthCallbackUrl(state, skapi.session);
                clearGoogleOAuthStart();
                window.location.href = oauthRedirectUri;
                return 'redirecting';
            }

            clearGoogleOAuthStart();
            // Same as the email/password login path: register an MCP OAuth
            // client and start the authorization-code flow before landing
            // on /my-projects. Browser is leaving the SPA.
            try {
                await beginMcpOAuthOnLogin("/my-projects");
                return 'redirecting';
            } catch (err) {
                console.error("MCP OAuth bootstrap failed", err);
                // Fall through so the user isn't stranded if MCP is down.
            }
            window.location.href = "/my-projects";
            return 'redirecting';
        } catch (err: any) {
            if (!(err instanceof SkapiError)) throw err;

            if (err.code === "ACCOUNT_EXISTS") {
                let merge = confirm(
                    "An account with this Google account already exists.\nWould you like to merge accounts?\nOnce merged, you cannot login from the previous method.",
                );
                if (merge) {
                    await skapi.openIdLogin({
                        id: "by_skapi",
                        token: data.access_token,
                        merge: ["name"],
                    });

                    if(urlParams.get("state")) {
                        const state = urlParams.get("state")!;
                        const stashed = readStashedInboundOAuth(state);
                        oauthRedirectUri = stashed
                            ? genOAuthCallbackUrl(state, skapi.session, stashed)
                            : genOAuthCallbackUrl(state, skapi.session);
                        clearGoogleOAuthStart();
                        window.location.href = oauthRedirectUri;
                        return 'redirecting';
                    }
                }
                clearGoogleOAuthStart();
                // Same as the non-merge path: kick off MCP OAuth before /my-projects.
                try {
                    await beginMcpOAuthOnLogin("/my-projects");
                    return 'redirecting';
                } catch (err) {
                    console.error("MCP OAuth bootstrap failed", err);
                }
                window.location.href = "/my-projects";
                return 'redirecting';
            }

            if (
                err.message === "Account needs to be confirmed." ||
                err.message === "The account needs to be confirmed."
            ) {
                openIdLoginError.value =
                    "Your account needs to be confirmed. Please check your email for the confirmation link.";
                clearGoogleOAuthStart();
                removeLoader();
                return;
            }

            if (err.message.includes("NOT_EXISTS")) {
                openIdLoginError.value = "The account does not exist.";
                clearGoogleOAuthStart();
                removeLoader();
                return;
            }

            // 처리되지 않은 openIdLogin 에러
            openIdLoginError.value = "Login failed. Please try again.";
            clearGoogleOAuthStart();
            removeLoader();

            throw err;
        }
};